import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../utils/supabase';
import { DEFAULT_CONTENT } from '../utils/defaultContent';

// =====================================================================
// SiteContentContext
//
// Loads the `store_content` row from app_settings on mount, deep-merges
// it with DEFAULT_CONTENT, and exposes the result via useSiteContent().
//
// To avoid a "flash of old content" on page load, the last-fetched
// content is cached in localStorage. On mount we use the cached value
// as the initial state so the very first render already has the right
// headlines/colors/fonts, then we fetch from Supabase in the background
// and only re-render if anything actually changed.
//
// Also applies brand colors as CSS custom properties on :root, so any
// component that wants to be themeable can use var(--tapas-primary)
// etc. instead of hardcoding hex values. Fonts are auto-loaded from
// Google Fonts when they differ from the defaults.
// =====================================================================

// Draft-preview mode: when the store is loaded with `?preview=draft`, it
// reads the draft rows from app_settings instead of the live ones. This
// lets the dashboard editor iframe show unpushed changes without
// affecting real visitors. Cache keys are namespaced so draft and live
// never overwrite each other in localStorage.
function isDraftPreview() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('preview') === 'draft';
  } catch {
    return false;
  }
}

const IS_DRAFT = isDraftPreview();

// Two app_settings rows can hold storefront content:
//   * store_content (v1)    — older flat content (brand/typography/page copy)
//   * store_content_v2      — block-tree authored by the dashboard
//                             WebsiteEditor (src/pages/WebsiteEditor.js)
// Historically these were disconnected: editor wrote v2, storefront only
// read v1. We now optionally fetch BOTH and deep-merge (v2 wins) when the
// `REACT_APP_USE_BLOCKS_V2` env var is truthy, OR when the URL carries
// `?v2=1`. The flag stays off by default until we re-seed v2 with the
// current live design — otherwise the stale v2 snapshot would clobber
// real content. Draft preview always layers both rows so the editor
// iframe shows the editor's own writes.
function v2Enabled() {
  if (IS_DRAFT) return true;
  if (typeof process !== 'undefined' && process.env?.REACT_APP_USE_BLOCKS_V2) return true;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('v2') === '1';
  } catch {
    return false;
  }
}
const USE_V2 = v2Enabled();

const CONTENT_ROW_KEYS = (() => {
  const liveBase = IS_DRAFT ? 'store_content_draft' : 'store_content';
  const v2Row    = IS_DRAFT ? 'store_content_v2_draft' : 'store_content_v2';
  return USE_V2 ? [liveBase, v2Row] : [liveBase];
})();
const CACHE_KEY = IS_DRAFT ? 'tapas_store_content_draft_v1' : 'tapas_store_content_v1';

function loadFromCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return stripDeletedPages(deepMerge(DEFAULT_CONTENT, parsed));
  } catch {
    return null;
  }
}

function saveToCache(rawValue) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(rawValue));
  } catch {}
}

const SiteContentCtx = createContext(DEFAULT_CONTENT);

export function useSiteContent() {
  return useContext(SiteContentCtx);
}

// Pages a user deleted are tracked in `_deleted_pages` on the content
// blob so the deep-merge with DEFAULT_CONTENT doesn't resurrect them.
// Strip them after every merge.
function stripDeletedPages(content) {
  const deleted = content?._deleted_pages;
  if (!Array.isArray(deleted) || deleted.length === 0) return content;
  if (!content?.pages) return content;
  const nextPages = { ...content.pages };
  for (const k of deleted) delete nextPages[k];
  return { ...content, pages: nextPages };
}

// Deep merge of two plain objects. Right side wins. Arrays are replaced.
function deepMerge(base, override) {
  if (!override || typeof override !== 'object') return base;
  const out = { ...base };
  for (const k of Object.keys(override)) {
    const bv = base[k];
    const ov = override[k];
    if (bv && typeof bv === 'object' && !Array.isArray(bv) && ov && typeof ov === 'object' && !Array.isArray(ov)) {
      out[k] = deepMerge(bv, ov);
    } else if (ov !== null && ov !== undefined && ov !== '') {
      out[k] = ov;
    }
  }
  return out;
}

// Apply per-element CSS override rules by injecting a single <style>
// tag whose rules target [data-editable="..."] selectors. Each rule
// uses !important so it wins over inline styles hardcoded in JSX.
// Supports reserved sub-keys `_hover` and `_active` which emit
// `:hover` and `:active` pseudo-class rules respectively.
//
// Phase 3: Also supports named classes. `classes[name]` holds styles
// shared across many elements; `elementClasses[path]` maps each path
// to its class name. The class's styles are merged UNDER the element's
// own styles (element wins on conflicts) so staff can fine-tune one
// instance without detaching from the class.
function applyElementStyles(elementStyles, classes, elementClasses) {
  const existing = document.getElementById('tapas-element-styles');
  if (existing) existing.remove();

  const buildDecls = (props) => Object.entries(props)
    // Reserved sub-keys: `_hover` / `_active` / `_anim_*` (handled
    // elsewhere) and `::selector` (emitted as a nested rule below).
    // Filter them out so they don't leak into the property declarations.
    .filter(([k, v]) => !k.startsWith('_') && !k.startsWith('::') && v !== undefined && v !== null && v !== '')
    .map(([k, v]) => {
      const cssProp = k.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${cssProp}: ${v} !important`;
    })
    .join('; ');

  // For each path we may have BOTH class styles and element styles.
  // Merge them so a path under class `btn-primary` with an element
  // override of `color: red` still gets the class's padding + radius.
  const paths = new Set([
    ...Object.keys(elementStyles || {}),
    ...Object.keys(elementClasses || {}),
  ]);

  const rules = [];
  for (const path of paths) {
    const className = elementClasses?.[path];
    const classStyle = className ? (classes?.[className] || {}) : {};
    const ownStyle   = elementStyles?.[path] || {};
    // Normal = class ∪ own (own wins)
    const normalMerged = { ...classStyle, ...ownStyle };
    // Pseudo states are merged per-key too.
    const hoverMerged = { ...(classStyle._hover || {}), ...(ownStyle._hover || {}) };
    const activeMerged = { ...(classStyle._active || {}), ...(ownStyle._active || {}) };

    const safePath = String(path).replace(/"/g, '\\"');
    const normalDecls = buildDecls(normalMerged);
    if (normalDecls) rules.push(`[data-editable="${safePath}"] { ${normalDecls}; }`);
    if (Object.keys(hoverMerged).length) {
      const hoverDecls = buildDecls(hoverMerged);
      if (hoverDecls) rules.push(`[data-editable="${safePath}"]:hover { ${hoverDecls}; }`);
    }
    if (Object.keys(activeMerged).length) {
      const activeDecls = buildDecls(activeMerged);
      if (activeDecls) rules.push(`[data-editable="${safePath}"]:active { ${activeDecls}; }`);
    }

    // Per-element sub-overrides — any subkey under ownStyle that starts
    // with "::" is a CSS selector relative to the block root. Emits one
    // rule per subkey: `[data-editable="…"] {selector} { … }`. The
    // editor's element-level inspector writes here when the user
    // tweaks color / font-size / padding on a sub-element.
    for (const subKey of Object.keys(ownStyle)) {
      if (!subKey.startsWith('::')) continue;
      const subStyles = ownStyle[subKey];
      if (!subStyles || typeof subStyles !== 'object') continue;
      const subDecls = buildDecls(subStyles);
      if (!subDecls) continue;
      const childSel = subKey.slice(2); // strip "::"
      rules.push(`[data-editable="${safePath}"] ${childSel} { ${subDecls}; }`);
    }
  }

  if (!rules.length) return;
  const style = document.createElement('style');
  style.id = 'tapas-element-styles';
  style.innerHTML = rules.join('\n');
  document.head.appendChild(style);
}

// Phase 5: Element animations. Reads _anim_* keys from element_styles
// + classes (class values seed the config; element values override) and
// wires up scroll-in animations via a module-level IntersectionObserver
// plus CSS-only hover effects. All CSS lives in one injected <style>
// tag so there's zero runtime cost for pages with no animated elements.
const ANIMATION_CSS = `
@keyframes tapas-a-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes tapas-a-slide-up { from { opacity: 0; transform: translateY(24px) } to { opacity: 1; transform: none } }
@keyframes tapas-a-slide-left { from { opacity: 0; transform: translateX(-24px) } to { opacity: 1; transform: none } }
@keyframes tapas-a-slide-right { from { opacity: 0; transform: translateX(24px) } to { opacity: 1; transform: none } }
@keyframes tapas-a-zoom { from { opacity: 0; transform: scale(0.92) } to { opacity: 1; transform: scale(1) } }

[data-tapas-anim] { opacity: 0; will-change: opacity, transform; }
[data-tapas-anim][data-tapas-anim-in] {
  opacity: 1;
  animation: tapas-a-fade var(--tapas-anim-duration,600ms) var(--tapas-anim-easing,ease-out) var(--tapas-anim-delay,0ms) both;
}
[data-tapas-anim="slide-up"][data-tapas-anim-in]    { animation-name: tapas-a-slide-up }
[data-tapas-anim="slide-left"][data-tapas-anim-in]  { animation-name: tapas-a-slide-left }
[data-tapas-anim="slide-right"][data-tapas-anim-in] { animation-name: tapas-a-slide-right }
[data-tapas-anim="zoom"][data-tapas-anim-in]        { animation-name: tapas-a-zoom }

[data-tapas-hover] { transition: transform 220ms ease-out, box-shadow 220ms ease-out, filter 220ms ease-out; }
[data-tapas-hover="lift"]:hover  { transform: translateY(-4px); box-shadow: 0 14px 32px rgba(0,0,0,0.14); }
[data-tapas-hover="scale"]:hover { transform: scale(1.04); }
[data-tapas-hover="glow"]:hover  { box-shadow: 0 0 0 3px rgba(99,102,241,0.35); }
[data-tapas-hover="tilt"]:hover  { transform: perspective(600px) rotateX(4deg) rotateY(-4deg); }

/* Navbar composite (Lane C1). Kept in sync with the staff-app's
   WebsiteEditor.anim.js — the two apps can't easily share code, so
   edits go in both places. The runtime-toggle logic is below in
   wireNavbarToggles(). */
[data-tapas-navbar] {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 14px 22px;
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,0.08);
  position: relative;
}
[data-tapas-navbar] .tapas-navbar-brand { font-weight: 700; font-size: 18px; }
[data-tapas-navbar] .tapas-navbar-menu {
  display: flex; list-style: none; margin: 0 0 0 auto; padding: 0; gap: 24px;
}
[data-tapas-navbar] .tapas-navbar-menu > li { margin: 0; padding: 0; }
[data-tapas-navbar] .tapas-navbar-link {
  text-decoration: none; color: inherit; font-size: 14px; line-height: 1.4;
}
[data-tapas-navbar] .tapas-navbar-toggle {
  display: none; margin-left: auto; background: transparent; border: 0;
  font-size: 20px; line-height: 1; cursor: pointer; padding: 4px 8px; color: inherit;
}
@media (max-width: 767px) {
  [data-tapas-navbar] { flex-wrap: wrap; }
  [data-tapas-navbar] .tapas-navbar-toggle { display: block; }
  [data-tapas-navbar] .tapas-navbar-menu {
    display: none; width: 100%; flex-direction: column; order: 10;
    margin-left: 0; padding-top: 8px; gap: 10px;
  }
  [data-tapas-navbar][data-tapas-navbar-open] .tapas-navbar-menu { display: flex; }
}
`;

// Navbar hamburger toggle — attached once to document, delegates
// click-events to find data-tapas-navbar-toggle → walk up to
// data-tapas-navbar → flip data-tapas-navbar-open. Idempotent so
// hot-reload in dev doesn't double-wire.
let tapasNavbarWired = false;
function wireNavbarToggles() {
  if (tapasNavbarWired || typeof document === 'undefined') return;
  tapasNavbarWired = true;
  document.addEventListener('click', (e) => {
    let tgl = e.target;
    while (tgl && tgl !== document.body) {
      if (tgl.hasAttribute?.('data-tapas-navbar-toggle')) break;
      tgl = tgl.parentElement;
    }
    if (!tgl || tgl === document.body) return;
    let nav = tgl.parentElement;
    while (nav && nav !== document.body) {
      if (nav.hasAttribute?.('data-tapas-navbar')) break;
      nav = nav.parentElement;
    }
    if (!nav || nav === document.body) return;
    if (nav.hasAttribute('data-tapas-navbar-open')) {
      nav.removeAttribute('data-tapas-navbar-open');
    } else {
      nav.setAttribute('data-tapas-navbar-open', '');
    }
  });
}

let tapasAnimObserver = null;
function applyElementAnimations(elementStyles, classes, elementClasses) {
  // Inject stylesheet once (idempotent).
  if (!document.getElementById('tapas-element-animations')) {
    const s = document.createElement('style');
    s.id = 'tapas-element-animations';
    s.innerHTML = ANIMATION_CSS;
    document.head.appendChild(s);
  }
  // Clear previously-applied attributes on every apply so removing an
  // animation via the inspector actually takes effect.
  document.querySelectorAll('[data-tapas-anim]').forEach(el => {
    el.removeAttribute('data-tapas-anim');
    el.removeAttribute('data-tapas-anim-in');
    el.style.removeProperty('--tapas-anim-duration');
    el.style.removeProperty('--tapas-anim-delay');
    el.style.removeProperty('--tapas-anim-easing');
  });
  document.querySelectorAll('[data-tapas-hover]').forEach(el => {
    el.removeAttribute('data-tapas-hover');
  });

  const resolvePath = (path) => {
    const className = elementClasses?.[path];
    const fromClass = className ? (classes?.[className] || {}) : {};
    const own = elementStyles?.[path] || {};
    const pick = (k) => own[k] !== undefined ? own[k] : fromClass[k];
    return {
      scrollIn: pick('_anim_scroll_in'),
      hover:    pick('_anim_hover'),
      duration: pick('_anim_duration'),
      delay:    pick('_anim_delay'),
      easing:   pick('_anim_easing'),
    };
  };

  const allPaths = new Set([
    ...Object.keys(elementStyles || {}),
    ...Object.keys(elementClasses || {}),
  ]);

  // Lazily create a single IntersectionObserver that flips
  // data-tapas-anim-in on intersecting elements.
  if (!tapasAnimObserver && typeof IntersectionObserver !== 'undefined') {
    tapasAnimObserver = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) e.target.setAttribute('data-tapas-anim-in', '');
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
  }
  const observer = tapasAnimObserver;

  for (const path of allPaths) {
    const cfg = resolvePath(path);
    if (!cfg.scrollIn && !cfg.hover) continue;
    const els = document.querySelectorAll(`[data-editable="${path}"]`);
    els.forEach(el => {
      if (cfg.scrollIn) {
        el.setAttribute('data-tapas-anim', cfg.scrollIn);
        if (cfg.duration) el.style.setProperty('--tapas-anim-duration', `${cfg.duration}ms`);
        if (cfg.delay)    el.style.setProperty('--tapas-anim-delay',    `${cfg.delay}ms`);
        if (cfg.easing)   el.style.setProperty('--tapas-anim-easing',   cfg.easing);
        if (observer) observer.observe(el);
      }
      if (cfg.hover) el.setAttribute('data-tapas-hover', cfg.hover);
    });
  }
}

// Apply brand + typography + button tokens to :root as CSS custom props.
// Phase 4: Merges the active mode's overrides on top of `brand` before
// resolving CSS vars, so switching modes changes every themed element.
function applyTheme(content) {
  const root = document.documentElement;
  // Resolve the effective brand = brand + modes[active_mode] overrides.
  const baseBrand = content?.brand || {};
  const activeMode = content?.active_mode;
  const modeOverrides = (activeMode && content?.modes?.[activeMode]) || {};
  const b = { ...baseBrand, ...modeOverrides };
  if (b) {
    root.style.setProperty('--tapas-primary',        b.primary_color);
    root.style.setProperty('--tapas-primary-dark',   b.primary_color_dark);
    root.style.setProperty('--tapas-primary-light',  b.primary_color_light);
    root.style.setProperty('--tapas-accent',         b.accent_color);
    root.style.setProperty('--tapas-accent-dark',    b.accent_color_dark);
    root.style.setProperty('--tapas-cream',          b.cream_color);
    root.style.setProperty('--tapas-sand',           b.sand_color);
    root.style.setProperty('--tapas-heading-font',   `"${b.heading_font}", serif`);
    root.style.setProperty('--tapas-body-font',      `"${b.body_font}", sans-serif`);
    // Expose the active mode for any components that want to branch on it
    // (e.g. dark-mode-only SVGs, adjusted shadows).
    if (activeMode) root.setAttribute('data-mode', activeMode);
    else root.removeAttribute('data-mode');
  }

  const t = content?.typography;
  if (t) {
    root.style.setProperty('--tapas-h-xxl-size',   `${t.heading_xxl_size || 72}px`);
    root.style.setProperty('--tapas-h-xl-size',    `${t.heading_xl_size || 42}px`);
    root.style.setProperty('--tapas-h-l-size',     `${t.heading_l_size || 32}px`);
    root.style.setProperty('--tapas-h-color',      t.heading_color || (b?.primary_color || '#2C1810'));
    root.style.setProperty('--tapas-h-weight',     t.heading_weight || '800');
    root.style.setProperty('--tapas-body-size',    `${t.body_size || 16}px`);
    root.style.setProperty('--tapas-body-color',   t.body_color || '#5C3A1E');
    root.style.setProperty('--tapas-eyebrow-size', `${t.eyebrow_size || 11}px`);
    root.style.setProperty('--tapas-eyebrow-tracking', t.eyebrow_tracking || '2.5px');
  }

  const btn = content?.buttons;
  if (btn) {
    root.style.setProperty('--tapas-btn-radius',         `${btn.radius ?? 50}px`);
    root.style.setProperty('--tapas-btn-padding',        `${btn.padding_y ?? 14}px ${btn.padding_x ?? 32}px`);
    root.style.setProperty('--tapas-btn-font-size',      `${btn.font_size ?? 15}px`);
    root.style.setProperty('--tapas-btn-font-weight',    btn.font_weight || '700');
    root.style.setProperty('--tapas-btn-text-transform', btn.text_transform || 'none');
    root.style.setProperty('--tapas-btn-letter-spacing', btn.letter_spacing || '0.5px');
  }

  // Per-element overrides (rendered from [data-editable] attributes).
  applyElementStyles(content?.element_styles, content?.classes, content?.element_classes);
  // Phase 5: wire up scroll-in + hover animations.
  applyElementAnimations(content?.element_styles, content?.classes, content?.element_classes);
  // Lane C1: one-shot delegate for navbar hamburger toggles.
  wireNavbarToggles();
}

// Load any Google Fonts the user has picked that aren't already loaded.
// We track loaded families to avoid injecting duplicate <link> tags.
const loadedFonts = new Set(['Playfair Display', 'Lato']);
function loadGoogleFont(family) {
  if (!family || loadedFonts.has(family)) return;
  loadedFonts.add(family);
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;600;700;800&display=swap`;
  const existing = document.querySelector(`link[data-tapas-font="${family}"]`);
  if (existing) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.setAttribute('data-tapas-font', family);
  document.head.appendChild(link);
}

// Walk up from `target` to find the nearest [data-editable="pages.…"]
// ancestor (the block root) AND build a CSS-ish selector for the leaf.
// Mirrors the editor-side computeElementSelector — kept here so the
// editor doesn't need to peek into the iframe's DOM (cross-origin
// would block it anyway).
function describeClickedElement(target, doc) {
  let blockEl = target;
  while (blockEl && blockEl !== doc.body) {
    const e = blockEl.getAttribute?.('data-editable');
    if (e && e.startsWith('pages.')) break;
    blockEl = blockEl.parentElement;
  }
  if (!blockEl || blockEl === doc.body) return null;
  const segs = [];
  // Walk up from target to the block root, collecting tag:nth-of-type
  // segments along the way. The .filter callback below references the
  // loop-scoped `node`, which eslint's no-loop-func can't statically
  // verify is safe (the loop body runs to completion before the next
  // iteration so the closure capture is fine).
  // eslint-disable-next-line no-loop-func
  for (let cur = target; cur && cur !== blockEl; cur = cur.parentElement) {
    const parent = cur.parentElement;
    if (!parent) break;
    const node = cur;
    const tag = node.tagName.toLowerCase();
    const sameTag = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
    const idx = sameTag.indexOf(node) + 1;
    segs.unshift(sameTag.length > 1 ? `${tag}:nth-of-type(${idx})` : tag);
  }
  const rect = target.getBoundingClientRect();
  return {
    editable: blockEl.getAttribute('data-editable'),
    selector: segs.join(' > ') || null,
    tag: target.tagName.toLowerCase(),
    rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
    text: (target.textContent || '').slice(0, 80).trim(),
  };
}

export function SiteContentProvider({ children }) {
  // Initial state: cached content if we have it, otherwise defaults.
  // useState's lazy initializer runs exactly once on first render,
  // so this is synchronous — no flash.
  const [content, setContent] = useState(() => loadFromCache() || DEFAULT_CONTENT);

  // Apply theme from whatever we have RIGHT NOW (cache or defaults).
  // This runs on first render, before any paint, so CSS variables
  // and fonts are in place before the browser draws anything.
  useEffect(() => {
    applyTheme(content);
    if (content.brand?.heading_font) loadGoogleFont(content.brand.heading_font);
    if (content.brand?.body_font)    loadGoogleFont(content.brand.body_font);
    // Only needs to re-run when the cached/default content first loads.
    // Subsequent theme updates happen inside the fetch effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Editor-iframe outbound bridge: when running in preview-draft mode,
  // intercept clicks on any [data-editable="pages.…"] element and
  // postMessage the selection up to the editor parent. Lets the editor
  // canvas render selection overlays without needing same-origin DOM
  // access into this storefront iframe (which would be blocked across
  // localhost:3000 ↔ localhost:3001 anyway).
  useEffect(() => {
    if (!IS_DRAFT) return;
    const onClick = (e) => {
      // Editor-mode: hijack ALL clicks so navigation links don't yank
      // staff away from the canvas. Modifier-clicks (cmd/ctrl/middle)
      // still pass through so staff can intentionally open in a new tab.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      const info = describeClickedElement(e.target, document);
      if (!info) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        window.parent.postMessage({ type: 'tapas:select', ...info }, '*');
      } catch {}
    };
    // capture-phase so we run before any inner handler stops propagation.
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // Inline text editing bridge — double-click any element with a single
  // text-node child to make it contentEditable. On blur, postMessage
  // the new text + the parent block's `data-editable` path UP. The
  // editor matches the OLD text against the block's props (string-
  // equality lookup) and writes the matching prop. Esc cancels.
  // Skips elements with mixed/multiple children to avoid corrupting
  // nested HTML (e.g. a heading that contains an <em>).
  useEffect(() => {
    if (!IS_DRAFT) return;
    let activeEl = null;
    let activeOriginal = '';
    let activeBlockEditable = null;

    const finish = (commit) => {
      if (!activeEl) return;
      activeEl.removeAttribute('contenteditable');
      activeEl.style.removeProperty('outline');
      activeEl.style.removeProperty('cursor');
      activeEl.style.removeProperty('background');
      const newText = activeEl.textContent || '';
      const oldText = activeOriginal;
      const editable = activeBlockEditable;
      const elRef = activeEl;
      activeEl = null;
      activeOriginal = '';
      activeBlockEditable = null;
      if (!commit) {
        // Restore on cancel.
        elRef.textContent = oldText;
        return;
      }
      if (newText === oldText) return;
      try {
        window.parent.postMessage({
          type: 'tapas:text-edit',
          editable,
          oldText,
          newText,
        }, '*');
      } catch {}
    };

    const onDblClick = (e) => {
      // Walk up to find the [data-editable="pages.…"] block root.
      let blockEl = e.target;
      while (blockEl && blockEl !== document.body) {
        const ed = blockEl.getAttribute?.('data-editable');
        if (ed && ed.startsWith('pages.')) break;
        blockEl = blockEl.parentElement;
      }
      if (!blockEl || blockEl === document.body) return;
      // Only enable inline edit on elements whose ONLY child is a text
      // node — avoids stomping nested <em>/<a>/<img> children.
      const target = e.target;
      if (target.children.length > 0) return;
      if (!target.textContent || !target.textContent.trim()) return;
      // Already editing this one? leave it.
      if (activeEl === target) return;
      // Commit any prior edit first.
      finish(true);
      e.preventDefault();
      e.stopPropagation();
      activeEl = target;
      activeOriginal = target.textContent || '';
      activeBlockEditable = blockEl.getAttribute('data-editable');
      target.setAttribute('contenteditable', 'plaintext-only');
      target.style.outline = '2px solid #f97316';
      target.style.cursor = 'text';
      target.style.background = 'rgba(249, 115, 22, 0.08)';
      // Tell window editing started so SiteContent doesn't re-render
      // while a contentEditable caret is active.
      window.__tapasInlineEditing = true;
      // Focus + select all on next frame so the user can just type.
      requestAnimationFrame(() => {
        target.focus();
        const range = document.createRange();
        range.selectNodeContents(target);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      });
    };

    const onBlur = (e) => {
      if (e.target === activeEl) {
        window.__tapasInlineEditing = false;
        finish(true);
      }
    };

    const onKey = (e) => {
      if (!activeEl) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        window.__tapasInlineEditing = false;
        finish(false);
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        window.__tapasInlineEditing = false;
        finish(true);
        if (activeEl) activeEl.blur();
      }
    };

    document.addEventListener('dblclick', onDblClick, true);
    document.addEventListener('blur', onBlur, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('dblclick', onDblClick, true);
      document.removeEventListener('blur', onBlur, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, []);

  // Hover bridge — same shape as click but throttled, so the editor can
  // draw a hover outline without polling the iframe's DOM.
  useEffect(() => {
    if (!IS_DRAFT) return;
    let lastEditable = null;
    const onOver = (e) => {
      const info = describeClickedElement(e.target, document);
      const next = info?.editable || null;
      if (next === lastEditable) {
        // Same block, but re-emit so the rect updates with the new
        // sub-element under the cursor.
      }
      lastEditable = next;
      try {
        window.parent.postMessage(
          info
            ? { type: 'tapas:hover', ...info }
            : { type: 'tapas:hover-clear' },
          '*',
        );
      } catch {}
    };
    document.addEventListener('mouseover', onOver, true);
    return () => document.removeEventListener('mouseover', onOver, true);
  }, []);

  // Live preview bridge: when running inside the dashboard editor iframe,
  // listen for postMessage updates so changes appear instantly without a
  // DB roundtrip. Dashboard sends the entire content blob on every edit.
  useEffect(() => {
    if (!IS_DRAFT) return;
    const handler = (event) => {
      const msg = event.data;
      if (!msg || typeof msg !== 'object') return;
      if (msg.type !== 'tapas:apply-content') return;
      if (!msg.content || typeof msg.content !== 'object') return;
      // Pause while the user is inline-editing a text node on the canvas
      // — a React re-render would destroy the contentEditable caret.
      // StoreEditorSync sets this flag while an edit is active.
      if (typeof window !== 'undefined' && window.__tapasInlineEditing) {
        // Still apply the theme so colors/fonts live-update from the
        // right panel, but don't re-render the content tree.
        try { applyTheme(deepMerge(DEFAULT_CONTENT, msg.content)); } catch {}
        return;
      }
      try {
        const merged = stripDeletedPages(deepMerge(DEFAULT_CONTENT, msg.content));
        setContent(merged);
        applyTheme(merged);
        if (merged.brand?.heading_font) loadGoogleFont(merged.brand.heading_font);
        if (merged.brand?.body_font)    loadGoogleFont(merged.brand.body_font);
      } catch (err) {
        console.warn('[SiteContent] apply-content failed', err);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Background fetch: pull latest from Supabase, update only if different.
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // Fetch both rows in parallel. CONTENT_ROW_KEYS is ordered
        // [v1, v2]; v2 wins on deep-merge conflict so editor edits flow
        // straight to the storefront.
        const { data: rows } = await supabase
          .from('app_settings')
          .select('key, value')
          .in('key', CONTENT_ROW_KEYS);
        if (!mounted) return;

        const byKey = new Map((rows || []).map((r) => [r.key, r.value]));
        const parse = (v) => (v == null ? null : (typeof v === 'string' ? JSON.parse(v) : v));
        const v1 = parse(byKey.get(CONTENT_ROW_KEYS[0]));
        const v2 = parse(byKey.get(CONTENT_ROW_KEYS[1]));

        if (v1 || v2) {
          // Layered merge: defaults < v1 < v2.
          const layeredRaw = deepMerge(deepMerge({}, v1 || {}), v2 || {});
          const merged = stripDeletedPages(deepMerge(DEFAULT_CONTENT, layeredRaw));
          const currentStr = JSON.stringify(content);
          const mergedStr  = JSON.stringify(merged);
          if (currentStr !== mergedStr) {
            setContent(merged);
            applyTheme(merged);
            if (merged.brand?.heading_font) loadGoogleFont(merged.brand.heading_font);
            if (merged.brand?.body_font)    loadGoogleFont(merged.brand.body_font);
          }
          // Cache the layered raw value (pre-DEFAULT_CONTENT merge) so a
          // future DEFAULT_CONTENT bump can re-merge cleanly on next load.
          saveToCache(layeredRaw);
        } else {
          // No rows at all — reset to defaults and clear any stale cache.
          try { localStorage.removeItem(CACHE_KEY); } catch {}
          const currentStr = JSON.stringify(content);
          const defaultStr = JSON.stringify(DEFAULT_CONTENT);
          if (currentStr !== defaultStr) {
            setContent(DEFAULT_CONTENT);
            applyTheme(DEFAULT_CONTENT);
          }
        }
      } catch (err) {
        console.warn('[SiteContent] load failed, using cached/defaults:', err?.message || err);
      }
    })();

    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SiteContentCtx.Provider value={content}>
      {children}
    </SiteContentCtx.Provider>
  );
}
